/**
 * IndexedDBStore - Centralized Dexie Service
 *
 * Single source of truth for IndexedDB operations across MFE bundles. DB_NAME is defined ONLY here - no other services
 * should import Dexie directly.
 *
 * @example
 *   ```typescript
 *   // In your service
 *   private readonly store = inject(IndexedDBStore);
 *
 *   async loadData() {
 *     // Add table dynamically (version upgrade handled internally)
 *     await this.store.addTable('icd-codes', { primaryKey: 'icdCodeId', indexes: ['code', 'description'] });
 *
 *     // CRUD operations
 *     await this.store.bulkPut('icd-codes', data);
 *     const items = await this.store.getAll('icd-codes');
 *     const item = await this.store.getByKey('icd-codes', 123);
 *   }
 *   ```;
 */

import { inject, Injectable } from '@angular/core';
import Dexie from 'dexie';
import { applyEncryptionMiddleware, NON_INDEXED_FIELDS } from 'dexie-encrypted';
import { CryptoService } from './crypto.service';
import { StoreSchema, TableMeta } from './indexed-db.types';
import {
  ASSESSMENT_DB_NAME,
  ASSESSMENT_TABLE_NAMES,
  CACHE_EXPIRY_MS,
  LOOKUP_DB_NAME,
  LOOKUP_SCHEMA_VERSION,
  LOOKUP_TABLES,
} from './schema-registry';

// =============================================================================
// Constants - DB_NAME from schema registry
// =============================================================================

const DEFAULT_DB_NAME = LOOKUP_DB_NAME;
const META_TABLE = '_meta';
const LOOKUP_BULK_WRITE_CHUNK_SIZE = 2500;

type IndexedDBStoreGlobalState = {
  db: Dexie | null;
  schemas: Map<string, StoreSchema>;
  currentVersion: number;
  initialized: boolean;
  initializeLock: Promise<void> | null;
  addTableLock: Promise<void> | null;
  encryptionKeyBytes: Uint8Array | null;
};

// Global enabled flag shared across ALL databases
const GLOBAL_ENABLED_KEY = '__INDEXED_DB_ENABLED__';

function getGlobalEnabled(): boolean {
  const globalStore = globalThis as typeof globalThis & { [GLOBAL_ENABLED_KEY]?: boolean };
  if (globalStore[GLOBAL_ENABLED_KEY] === undefined) {
    globalStore[GLOBAL_ENABLED_KEY] = true; // Default to enabled
  }
  return globalStore[GLOBAL_ENABLED_KEY]!;
}

function setGlobalEnabled(enabled: boolean): void {
  const globalStore = globalThis as typeof globalThis & { [GLOBAL_ENABLED_KEY]?: boolean };
  globalStore[GLOBAL_ENABLED_KEY] = enabled;
}

// =============================================================================
// IndexedDBStore Service
// =============================================================================

@Injectable({ providedIn: 'root' })
export class IndexedDBStore {
  private _crypto: CryptoService | null = null;

  constructor() {
    try {
      this._crypto = inject(CryptoService, { optional: true });
    } catch {
      // Safely handle instantiation outside Angular injection context (e.g., forDatabase)
      this._crypto = CryptoService.getInstance();
    }
    if (!this._crypto) {
      this._crypto = CryptoService.getInstance();
    }
  }

  private get crypto(): CryptoService | null {
    if (!this._crypto) {
      this._crypto = CryptoService.getInstance();
    }
    return this._crypto;
  }

  private static getGlobalState(dbName: string = DEFAULT_DB_NAME): IndexedDBStoreGlobalState {
    const key = `__INDEXED_DB_STORE_${dbName}__`;
    const globalStore = globalThis as typeof globalThis & Record<string, IndexedDBStoreGlobalState | undefined>;

    if (!globalStore[key]) {
      globalStore[key] = {
        db: null,
        schemas: new Map<string, StoreSchema>(),
        currentVersion: 1,
        initialized: false,
        initializeLock: null,
        addTableLock: null,
        encryptionKeyBytes: null,
      };
    }

    return globalStore[key]!;
  }

  /** Database name for this instance. Default: 'symphony-web-cache'. */
  private _dbName = DEFAULT_DB_NAME;

  /** Get the database name for this instance. */
  get dbName(): string {
    return this._dbName;
  }

  private static getInstanceMap(): Map<string, IndexedDBStore> {
    const key = '__INDEXED_DB_STORE_INSTANCES__';
    const g = globalThis as typeof globalThis & Record<string, Map<string, IndexedDBStore> | undefined>;
    if (!g[key]) {
      g[key] = new Map<string, IndexedDBStore>();
    }
    return g[key]!;
  }

  /**
   * Create or retrieve a standalone IndexedDBStore instance targeting a different database. Caches instances across MFE
   * bundles using globalThis to prevent duplicate allocations.
   *
   * @param dbName - The IndexedDB database name (e.g., 'symphony-web-assessment_u101')
   * @param crypto - Optional CryptoService instance if available in calling context
   */
  static forDatabase(dbName: string, crypto?: CryptoService | null): IndexedDBStore {
    const instances = IndexedDBStore.getInstanceMap();
    let instance = instances.get(dbName);
    if (!instance) {
      instance = new IndexedDBStore();
      instance._dbName = dbName;
      instances.set(dbName, instance);
    }
    if (crypto && !instance._crypto) {
      instance._crypto = crypto;
    }
    return instance;
  }

  /** Close this Dexie database connection if open. */
  close(): void {
    const g = this.global;
    if (g.db && g.db.isOpen()) {
      g.db.close();
      g.initialized = false;
      g.db = null;
      g.encryptionKeyBytes = null;
    }
  }

  /** Close all open IndexedDBStore database connections. */
  static closeAll(): void {
    const instances = IndexedDBStore.getInstanceMap();
    instances.forEach((store) => store.close());
    instances.clear();
  }

  /**
   * One-time background migration: Migrates any un-synced pending offline operations (pending-sync, sync-dead-letter,
   * id-remap) from the legacy shared database ('symphony-web-assessment') into the user's isolated database, then
   * deletes the legacy databases ('symphony-web-assessment' and 'symphony-web-medications') to free up browser
   * storage.
   *
   * Thread-safe and deduplicated via localStorage flag.
   */
  static async migrateLegacyAssessmentDatabase(
    targetStore: IndexedDBStore,
    crypto?: CryptoService | null
  ): Promise<void> {
    if (typeof window === 'undefined' || typeof indexedDB === 'undefined') {
      return;
    }

    const legacyDbName = ASSESSMENT_DB_NAME;
    const legacyMedicationDbName = 'symphony-web-medications';
    const targetDbName = targetStore.dbName;

    // Do nothing if target is the legacy database
    if (targetDbName === legacyDbName) {
      return;
    }

    const migrationKey = `__symphony_legacy_migrated_${targetDbName}__`;
    try {
      if (localStorage.getItem(migrationKey) === 'true') {
        return;
      }
    } catch {
      // localStorage may be restricted in private browsing
    }

    try {
      const exists = await Dexie.exists(legacyDbName).catch(() => false);
      if (!exists) {
        // Also check if legacy medications db exists to clean up
        const medExists = await Dexie.exists(legacyMedicationDbName).catch(() => false);
        if (medExists) {
          await Dexie.delete(legacyMedicationDbName).catch(() => {});
        }
        try {
          localStorage.setItem(migrationKey, 'true');
        } catch {}
        return;
      }

      console.info(
        `[IndexedDBStore] Found legacy database '${legacyDbName}'. Migrating pending offline operations to '${targetDbName}'...`
      );

      // Attempt to read pending operations from legacy database
      let legacyDb: Dexie | null = null;
      try {
        legacyDb = new Dexie(legacyDbName);
        const resolvedCrypto = crypto ?? CryptoService.getInstance();
        const keyBytes = await resolvedCrypto?.getKeyBytes();

        if (keyBytes) {
          applyEncryptionMiddleware(
            legacyDb,
            new Uint8Array(keyBytes),
            {
              [ASSESSMENT_TABLE_NAMES.PENDING_SYNC]: NON_INDEXED_FIELDS,
              [ASSESSMENT_TABLE_NAMES.SYNC_DEAD_LETTER]: NON_INDEXED_FIELDS,
              [ASSESSMENT_TABLE_NAMES.ID_REMAP]: NON_INDEXED_FIELDS,
            } as any,
            async () => {}
          );
        }

        legacyDb.version(10).stores({
          [ASSESSMENT_TABLE_NAMES.PENDING_SYNC]: '++id, assessmentId, method, timestamp',
          [ASSESSMENT_TABLE_NAMES.SYNC_DEAD_LETTER]: '++id, assessmentId, method, timestamp, failedAt',
          [ASSESSMENT_TABLE_NAMES.ID_REMAP]: 'localId, resourceType, createdAt',
        });

        await legacyDb.open();

        // 1. Migrate pending-sync operations
        try {
          const pendingOps = await legacyDb.table<Record<string, any>>(ASSESSMENT_TABLE_NAMES.PENDING_SYNC).toArray();
          if (pendingOps.length > 0) {
            console.info(`[IndexedDBStore] Migrating ${pendingOps.length} pending operations from legacy database...`);
            for (const op of pendingOps) {
              const { id, ...recordWithoutId } = op;
              await targetStore.add(ASSESSMENT_TABLE_NAMES.PENDING_SYNC, recordWithoutId);
            }
          }
        } catch (e) {
          console.warn('[IndexedDBStore] Could not read legacy pending-sync records (non-fatal):', e);
        }

        // 2. Migrate sync-dead-letter operations
        try {
          const deadLetters = await legacyDb
            .table<Record<string, any>>(ASSESSMENT_TABLE_NAMES.SYNC_DEAD_LETTER)
            .toArray();
          if (deadLetters.length > 0) {
            console.info(
              `[IndexedDBStore] Migrating ${deadLetters.length} dead-letter operations from legacy database...`
            );
            for (const op of deadLetters) {
              const { id, ...recordWithoutId } = op;
              await targetStore.add(ASSESSMENT_TABLE_NAMES.SYNC_DEAD_LETTER, recordWithoutId);
            }
          }
        } catch (e) {
          console.warn('[IndexedDBStore] Could not read legacy sync-dead-letter records (non-fatal):', e);
        }

        // 3. Migrate id-remap records
        try {
          const remaps = await legacyDb.table<Record<string, any>>(ASSESSMENT_TABLE_NAMES.ID_REMAP).toArray();
          if (remaps.length > 0) {
            for (const remap of remaps) {
              await targetStore.put(ASSESSMENT_TABLE_NAMES.ID_REMAP, remap);
            }
          }
        } catch (e) {
          console.warn('[IndexedDBStore] Could not read legacy id-remap records (non-fatal):', e);
        }
      } catch (err) {
        console.warn('[IndexedDBStore] Error reading legacy database (non-fatal):', err);
      } finally {
        if (legacyDb) {
          try {
            legacyDb.close();
          } catch {}
        }
      }

      // Clean up legacy databases to free disk space
      console.info(`[IndexedDBStore] Deleting obsolete databases '${legacyDbName}' and '${legacyMedicationDbName}'...`);
      await Dexie.delete(legacyDbName).catch(() => {});
      await Dexie.delete(legacyMedicationDbName).catch(() => {});

      try {
        localStorage.setItem(migrationKey, 'true');
      } catch {}
      console.info('[IndexedDBStore] Legacy database migration & cleanup complete.');
    } catch (err) {
      console.warn('[IndexedDBStore] Legacy database migration failed gracefully:', err);
    }
  }

  // ===========================================================================
  // Global state - shared via globalThis to work across MFE bundles
  // Each database name gets its own isolated state.
  // ===========================================================================

  private get global(): IndexedDBStoreGlobalState {
    return IndexedDBStore.getGlobalState(this._dbName);
  }

  private get db(): Dexie | null {
    return this.global.db;
  }

  // ===========================================================================
  // Initialization
  // ===========================================================================

  /**
   * Initialize the database with the schema registry. Uses LOOKUP_SCHEMA_VERSION and LOOKUP_TABLES for deterministic
   * versioning. Safe to call multiple times - only initializes once.
   *
   * For the default database (symphony-web-cache), this uses the centralized schema registry. For custom databases
   * (e.g., assessment), use initializeWithSchema() instead.
   */
  async initialize(): Promise<void> {
    // Default database uses the centralized schema registry
    if (this._dbName === DEFAULT_DB_NAME) {
      await this.initializeWithSchema(LOOKUP_SCHEMA_VERSION, LOOKUP_TABLES);
      return;
    }

    // Custom databases use minimal initialization (just _meta table)
    // They should call initializeWithSchema() explicitly
    const g = this.global;

    if (g.initialized && g.db) {
      return;
    }

    if (g.initializeLock) {
      return g.initializeLock;
    }

    const init = (async () => {
      if (g.initialized && g.db) {
        return;
      }

      const keyBytes = await this.crypto?.getKeyBytes();
      if (keyBytes) {
        g.encryptionKeyBytes = new Uint8Array(keyBytes);
      }

      // Register _meta table schema (not encrypted - just metadata)
      g.schemas.set(META_TABLE, { primaryKey: 'tableName', encrypted: false });

      g.db = this.createDexieInstance();
      g.db.version(g.currentVersion).stores({ [META_TABLE]: 'tableName' });
      await g.db.open();
      g.initialized = true;
    })();

    g.initializeLock = init;
    try {
      await init;
    } finally {
      g.initializeLock = null;
    }
  }

  /**
   * Initialize the database with a specific schema version and table definitions. Use this for deterministic versioning
   *
   * - No dynamic table creation.
   *
   * @example
   *   ```typescript
   *   await store.initializeWithSchema(ASSESSMENT_SCHEMA_VERSION, ASSESSMENT_TABLES);
   *   ```;
   *
   * @param version - Schema version number (bump when schema changes)
   * @param tables - Array of table definitions with name and schema
   */
  async initializeWithSchema(
    version: number,
    tables: ReadonlyArray<{ name: string; schema: StoreSchema }>
  ): Promise<void> {
    const g = this.global;

    if (g.initialized && g.db) {
      return;
    }

    if (g.initializeLock) {
      return g.initializeLock;
    }

    const init = (async () => {
      if (g.initialized && g.db) {
        return;
      }

      // Register all tables in schema map
      for (const t of tables) {
        g.schemas.set(t.name, t.schema);
      }

      // Set the version explicitly (no auto-increment)
      g.currentVersion = version;

      // Get encryption key bytes if available
      const keyBytes = await this.crypto?.getKeyBytes();
      if (keyBytes) {
        g.encryptionKeyBytes = new Uint8Array(keyBytes);
      }

      // Create Dexie instance with encryption middleware
      g.db = this.createDexieInstance();

      // Build full schema including _meta table
      const fullSchema = this.buildFullSchema();

      // Define the version with all tables
      g.db.version(version).stores(fullSchema);

      try {
        await g.db.open();
      } catch (error) {
        if (!this.isPrimaryKeyUpgradeError(error)) {
          throw error;
        }

        // IndexedDB does not support changing an object store primary key in place. Without recovery the database
        // stays unopenable forever and every consumer of this store fails.
        g.db.close();

        if (this._dbName === DEFAULT_DB_NAME) {
          // Lookup data is a rebuildable cache, so recreate the whole database.
          await Dexie.delete(this._dbName);
          g.db = this.createDexieInstance();
          g.db.version(version).stores(fullSchema);
          await g.db.open();
        } else {
          // Other databases can hold unsynced PHI, so only the stores whose primary key actually changed are dropped
          // and recreated; everything else survives the upgrade.
          const conflictingTables = await this.findPrimaryKeyConflicts(fullSchema);
          if (conflictingTables.length === 0) {
            throw error;
          }

          const dropSchema: Record<string, string | null> = { ...fullSchema };
          for (const tableName of conflictingTables) {
            dropSchema[tableName] = null;
          }

          g.db = this.createDexieInstance();
          // Fractional version lands between the installed version and the target one so Dexie runs the drop first.
          g.db.version(version - 0.5).stores(dropSchema as Record<string, string>);
          g.db.version(version).stores(fullSchema);
          await g.db.open();
        }
      }
      g.initialized = true;
    })();

    g.initializeLock = init;
    try {
      await init;
    } finally {
      g.initializeLock = null;
    }
  }

  /**
   * Creates a new Dexie instance with encryption middleware applied if key is available. This ensures encryption is
   * always applied when recreating the instance.
   */
  private createDexieInstance(): Dexie {
    const g = this.global;
    const db = new Dexie(this._dbName);

    if (g.encryptionKeyBytes) {
      // Encrypt by default unless explicitly set to false (opt-out model)
      const tableEncryption: Record<string, typeof NON_INDEXED_FIELDS> = {};
      let encryptedCount = 0;

      g.schemas.forEach((schema, tableName) => {
        // Encrypt unless explicitly set to false
        if (schema.encrypted !== false) {
          tableEncryption[tableName] = NON_INDEXED_FIELDS;
          encryptedCount++;
        }
      });

      if (encryptedCount > 0) {
        applyEncryptionMiddleware(db, g.encryptionKeyBytes, tableEncryption as any, async () => {});
      }
    }

    return db;
  }

  private isPrimaryKeyUpgradeError(error: unknown): boolean {
    return (
      (error as { name?: string } | undefined)?.name === 'UpgradeError' &&
      String((error as { message?: string } | undefined)?.message ?? '').includes(
        'Not yet support for changing primary key'
      )
    );
  }

  /** Names of tables whose persisted primary key differs from the one the target schema declares. */
  private async findPrimaryKeyConflicts(fullSchema: Record<string, string>): Promise<string[]> {
    // Opening without a declared version puts Dexie in dynamic mode, exposing the schema currently on disk.
    const probe = new Dexie(this._dbName);

    try {
      await probe.open();

      return probe.tables
        .filter((table) => {
          const declared = fullSchema[table.name];
          return declared !== undefined && this.readPrimaryKey(table.schema.primKey) !== declared.split(',')[0].trim();
        })
        .map((table) => table.name);
    } catch {
      return [];
    } finally {
      probe.close();
    }
  }

  private readPrimaryKey(primKey: { keyPath?: string | string[] | null; auto?: boolean }): string {
    const keyPath = primKey.keyPath;

    if (Array.isArray(keyPath)) {
      return `[${keyPath.join('+')}]`;
    }

    return primKey.auto ? `++${keyPath ?? ''}` : (keyPath ?? '');
  }

  /** Check if the store is initialized and ready. */
  isReady(): boolean {
    return this.global.initialized && this.global.db !== null;
  }

  // ===========================================================================
  // Feature Flag Control
  // ===========================================================================

  /**
   * Enable or disable IndexedDB storage globally across ALL databases. Call from shell on startup based on
   * environment.enableIndexedDB. When disabled, all services will fetch from API directly without caching.
   *
   * @param enabled - Whether to enable IndexedDB storage
   * @param dbName - Deprecated parameter (kept for backward compatibility, ignored)
   */
  static setEnabled(enabled: boolean, dbName?: string): void {
    setGlobalEnabled(enabled);
  }

  setEnabled(enabled: boolean): void {
    IndexedDBStore.setEnabled(enabled);
  }

  /**
   * Check if IndexedDB storage is enabled globally. This is a single flag shared across all databases.
   *
   * @param dbName - Deprecated parameter (kept for backward compatibility, ignored)
   */
  static isEnabled(dbName?: string): boolean {
    return getGlobalEnabled();
  }

  /** Check if IndexedDB storage is enabled globally. */
  isEnabled(): boolean {
    return IndexedDBStore.isEnabled();
  }

  // ===========================================================================
  // Table Management
  // ===========================================================================

  /**
   * Add a new table/store dynamically. Handles Dexie version upgrade automatically. Uses a promise chain to serialize
   * close/reopen cycles and prevent DatabaseClosedError.
   *
   * @param tableName - Name of the table to create
   * @param schema - Schema defining primary key and indexes
   */
  async addTable(tableName: string, schema: StoreSchema): Promise<void> {
    const g = this.global;

    if (!g.db) {
      await this.initialize();
    }

    // Fast path: already exists
    if (this.tableExists(tableName)) {
      return;
    }

    // Chain onto any pending addTable — read and write lock synchronously (no await in between)
    // This guarantees each close/reopen cycle completes before the next one starts.
    const previous = g.addTableLock || Promise.resolve();
    const current = previous.then(async () => {
      // Re-check after prior operations complete
      if (this.tableExists(tableName)) {
        return;
      }

      g.schemas.set(tableName, schema);

      // Refresh encryption key bytes if not already set (crypto may have initialized after IndexedDBStore)
      if (!g.encryptionKeyBytes) {
        const keyBytes = await this.crypto.getKeyBytes();
        if (keyBytes) {
          g.encryptionKeyBytes = new Uint8Array(keyBytes);
        }
      }

      // Close current instance
      g.db!.close();
      // Recreate Dexie instance WITH encryption middleware
      g.currentVersion++;
      g.db = this.createDexieInstance();
      const fullSchema = this.buildFullSchema();
      g.db.version(g.currentVersion).stores(fullSchema);
      await g.db.open();
    });
    g.addTableLock = current; // Set BEFORE awaiting — next caller chains onto this
    await current;
  }

  /**
   * Register multiple tables in a SINGLE version bump. Use when all tables are known upfront (e.g., AssessmentService).
   * Avoids cascade of close→reopen per table that addTable() causes.
   */
  async addTables(tables: Array<{ name: string; schema: StoreSchema }>): Promise<void> {
    const g = this.global;

    if (!g.db) {
      await this.initialize();
    }

    // Filter to only truly new tables
    const newTables = tables.filter((t) => !this.tableExists(t.name));
    if (newTables.length === 0) return;

    // Chain onto any pending addTable lock
    const previous = g.addTableLock || Promise.resolve();
    const current = previous.then(async () => {
      // Re-filter after prior operations complete
      const stillNew = newTables.filter((t) => !this.tableExists(t.name));
      if (stillNew.length === 0) return;

      for (const t of stillNew) {
        g.schemas.set(t.name, t.schema);
      }

      g.db!.close();
      g.currentVersion++;
      g.db = this.createDexieInstance();
      const fullSchema = this.buildFullSchema();
      g.db.version(g.currentVersion).stores(fullSchema);
      await g.db.open();
    });
    g.addTableLock = current;
    await current;
  }

  /** Check if a table exists in the current schema. */
  tableExists(tableName: string): boolean {
    return this.global.db?.tables.some((t) => t.name === tableName) ?? false;
  }

  // ===========================================================================
  // CRUD Operations
  // ===========================================================================

  /** Get all records from a table. */
  async getAll<T = any>(tableName: string): Promise<T[]> {
    return this.executeWithRetry(() => this.db!.table<T>(tableName).toArray());
  }

  /** Get a single record by primary key. */
  async getByKey<T = any>(tableName: string, key: any): Promise<T | undefined> {
    return this.executeWithRetry(() => this.db!.table<T>(tableName).get(key));
  }

  /**
   * Search records with filter function.
   *
   * @param tableName - Table to search
   * @param filterFn - Filter function
   * @param limit - Max results (default: all)
   */
  async filter<T = any>(tableName: string, filterFn: (item: T) => boolean, limit?: number): Promise<T[]> {
    return this.executeWithRetry(() => {
      const collection = this.db!.table<T>(tableName).filter(filterFn);
      return limit ? collection.limit(limit).toArray() : collection.toArray();
    });
  }

  /** Insert or update multiple records. */
  async bulkPut<T = any>(tableName: string, items: T[]): Promise<void> {
    await this.executeWithRetry(() => this.db!.table<T>(tableName).bulkPut(items));
  }

  /** Insert or update a single record (upsert). */
  async put<T = any>(tableName: string, item: T): Promise<void> {
    await this.executeWithRetry(() => this.db!.table<T>(tableName).put(item));
  }

  /** Insert a record, returning its auto-generated key (for ++id tables). */
  async add<T = any>(tableName: string, item: T): Promise<any> {
    return this.executeWithRetry(() => this.db!.table<T>(tableName).add(item));
  }

  /** Get count of records in a table. */
  async count(tableName: string): Promise<number> {
    return this.executeWithRetry(() => this.db!.table(tableName).count());
  }

  /** Clear all records from a table. */
  async clear(tableName: string): Promise<void> {
    await this.executeWithRetry(() => this.db!.table(tableName).clear());
  }

  /** Delete a single record by key. */
  async delete(tableName: string, key: any): Promise<void> {
    await this.executeWithRetry(() => this.db!.table(tableName).delete(key));
  }

  /** Delete multiple records by their primary keys. */
  async bulkDelete(tableName: string, keys: any[]): Promise<void> {
    if (!keys || keys.length === 0) return;
    await this.executeWithRetry(() => this.db!.table(tableName).bulkDelete(keys));
  }

  /**
   * Delete records matching an index value without fetching or decrypting the underlying entities. Uses Dexie's
   * index-only `.primaryKeys()` query and `.bulkDelete(keys)`. This guarantees zero decryption overhead and prevents
   * decryption failures on stale or corrupted records.
   *
   * @param tableName - Table name
   * @param indexName - Indexed column name
   * @param value - Index value to match
   */
  async deleteByIndex(tableName: string, indexName: string, value: any): Promise<void> {
    await this.executeWithRetry(async () => {
      const table = this.db!.table(tableName);
      const collection =
        typeof value === 'string'
          ? table.where(indexName).equalsIgnoreCase(value)
          : table.where(indexName).equals(value);
      const keys = await collection.primaryKeys();
      if (keys && keys.length > 0) {
        await table.bulkDelete(keys);
      }
    });
  }

  /** Get limited records from a table. */
  async limit<T = any>(tableName: string, count: number): Promise<T[]> {
    return this.executeWithRetry(() => this.db!.table<T>(tableName).limit(count).toArray());
  }

  // ===========================================================================
  // Search Operations (Indexed)
  // ===========================================================================

  /**
   * Prefix search on an indexed column (FAST - uses B-tree index). Case-insensitive.
   *
   * @example
   *   ```typescript
   *   // Find codes starting with "A00"
   *   await store.searchByPrefix('icd-codes', 'icdCode', 'A00', 10);
   *   ```;
   *
   * @param tableName - Table to search
   * @param column - Indexed column name
   * @param prefix - Prefix to match
   * @param limit - Max results (default: 10)
   */
  async searchByPrefix<T = any>(tableName: string, column: string, prefix: string, limit = 10): Promise<T[]> {
    await this.waitForLock();
    this.assertReady();
    return this.db!.table<T>(tableName).where(column).startsWithIgnoreCase(prefix).limit(limit).toArray();
  }

  /**
   * Substring search across multiple columns (SLOW - full table scan). Use for description searches where prefix won't
   * work.
   *
   * @example
   *   ```typescript
   *   // Find records containing "diabetes" in description
   *   await store.searchBySubstring('icd-codes', ['icdDescription'], 'diabetes', 10);
   *   ```;
   *
   * @param tableName - Table to search
   * @param columns - Columns to search in
   * @param term - Substring to find (case-insensitive)
   * @param limit - Max results (default: 10)
   */
  async searchBySubstring<T = any>(tableName: string, columns: string[], term: string, limit = 10): Promise<T[]> {
    await this.waitForLock();
    this.assertReady();
    const lowerTerm = term.toLowerCase();
    return this.db!.table<T>(tableName)
      .filter((item: any) =>
        columns.some((col) =>
          String(item[col] || '')
            .toLowerCase()
            .includes(lowerTerm)
        )
      )
      .limit(limit)
      .toArray();
  }

  /**
   * Search using a custom predicate function (FAST - filters at the database iteration level before loading into
   * memory). Dexie will stop iterating as soon as the limit is reached.
   *
   * @param tableName - Table to search
   * @param predicate - Function to test each item
   * @param limit - Max results (default: 10)
   */
  async searchWithPredicate<T = any>(tableName: string, predicate: (item: T) => boolean, limit = 10): Promise<T[]> {
    await this.waitForLock();
    this.assertReady();
    return this.db!.table<T>(tableName).filter(predicate).limit(limit).toArray();
  }

  /**
   * Paged predicate search for incremental loading.
   *
   * @param tableName - Table to search
   * @param predicate - Function to test each item
   * @param limit - Max results to return
   * @param offset - Number of matched rows to skip before collecting
   */
  async searchWithPredicatePage<T = any>(
    tableName: string,
    predicate: (item: T) => boolean,
    limit = 10,
    offset = 0
  ): Promise<T[]> {
    await this.waitForLock();
    this.assertReady();
    return this.db!.table<T>(tableName).filter(predicate).offset(offset).limit(limit).toArray();
  }

  /**
   * Returns primary-key boundaries that divide a table into approximately equal row-count chunks. Each returned value
   * is the inclusive first key of a chunk after the first one.
   */
  async getPrimaryKeyChunkBoundaries(tableName: string, chunkSize: number): Promise<any[]> {
    await this.waitForLock();
    this.assertReady();

    if (!Number.isInteger(chunkSize) || chunkSize < 1) {
      throw new Error('chunkSize must be a positive integer');
    }

    const table = this.db!.table(tableName);
    const primaryKeyPath = table.schema.primKey.keyPath;
    if (typeof primaryKeyPath !== 'string') {
      throw new Error(`Table "${tableName}" does not have a string primary key path`);
    }

    const count = await table.count();
    const boundaryOffsets: number[] = [];
    for (let offset = chunkSize; offset < count; offset += chunkSize) {
      boundaryOffsets.push(offset);
    }

    return Promise.all(
      boundaryOffsets.map(async (offset) => {
        const row = await table.orderBy(':id').offset(offset).first();
        return (row as Record<string, unknown>)[primaryKeyPath];
      })
    );
  }

  /** Returns the lowest and highest numeric primary keys without walking table offsets. */
  async getNumericPrimaryKeyBounds(tableName: string): Promise<{ min: number; max: number } | null> {
    await this.waitForLock();
    this.assertReady();

    const table = this.db!.table(tableName);
    const primaryKeyPath = table.schema.primKey.keyPath;
    if (typeof primaryKeyPath !== 'string') {
      throw new Error(`Table "${tableName}" does not have a string primary key path`);
    }

    const [firstRow, lastRow] = await Promise.all([
      table.orderBy(':id').first(),
      table.orderBy(':id').reverse().first(),
    ]);
    if (!firstRow || !lastRow) return null;

    const min = Number((firstRow as Record<string, unknown>)[primaryKeyPath]);
    const max = Number((lastRow as Record<string, unknown>)[primaryKeyPath]);
    if (!Number.isFinite(min) || !Number.isFinite(max)) {
      throw new Error(`Table "${tableName}" does not have numeric primary keys`);
    }

    return { min, max };
  }

  /**
   * Exact match on an indexed column (FAST - uses index). Case-insensitive.
   *
   * @param tableName - Table to search
   * @param column - Indexed column name
   * @param value - Exact value to match
   */
  async searchByEquals<T = any>(tableName: string, column: string, value: string): Promise<T[]> {
    await this.waitForLock();
    this.assertReady();
    return this.db!.table<T>(tableName).where(column).equalsIgnoreCase(value).toArray();
  }

  /**
   * Read rows matching any of the supplied values from one indexed column. This is intended for small lookup sets, such
   * as the active ICD codes being evaluated in an assessment.
   */
  async searchByAnyEquals<T = any>(tableName: string, column: string, values: string[]): Promise<T[]> {
    const uniqueValues = [...new Set(values.filter(Boolean))];
    if (uniqueValues.length === 0) return [];

    await this.waitForLock();
    this.assertReady();
    return this.db!.table<T>(tableName).where(column).anyOf(uniqueValues).toArray();
  }

  /**
   * Filter by indexed column using exact match (alias for searchByEquals for clarity). Useful for early-stage filtering
   * before applying other predicates.
   *
   * @param tableName - Table to search
   * @param indexName - Indexed column name
   * @param value - Exact value to match
   */
  async filterByIndex<T = any>(tableName: string, indexName: string, value: any): Promise<T[]> {
    await this.waitForLock();
    this.assertReady();
    if (typeof value === 'string') {
      return this.db!.table<T>(tableName).where(indexName).equalsIgnoreCase(value).toArray();
    }
    return this.db!.table<T>(tableName).where(indexName).equals(value).toArray();
  }

  // ===========================================================================
  // Meta Operations (cache expiration tracking)
  // ===========================================================================

  /** Get metadata for a table. */
  async getMeta(tableName: string): Promise<TableMeta | undefined> {
    await this.waitForLock();
    this.assertReady();
    return this.db!.table<TableMeta>(META_TABLE).get(tableName);
  }

  /** Get metadata for many tables in a single `_meta` read (results align with the requested order). */
  async getMetaBatch(tableNames: string[]): Promise<Array<TableMeta | undefined>> {
    if (tableNames.length === 0) return [];

    await this.waitForLock();
    this.assertReady();
    return this.db!.table<TableMeta>(META_TABLE).bulkGet(tableNames);
  }

  /** Set metadata for a table (updates timestamp automatically). */
  async setMeta(
    tableName: string,
    count: number,
    extras?: Pick<
      TableMeta,
      'sourceModifiedEpochMs' | 'lastSyncEpochMs' | 'expiryEpochMs' | 'lastStatus' | 'lookupConfigTableNames'
    >
  ): Promise<void> {
    await this.waitForLock();
    this.assertReady();
    await this.db!.table<TableMeta>(META_TABLE).put({
      tableName,
      timestamp: Date.now(),
      count,
      ...extras,
    });
  }

  /**
   * Atomically replace an existing table's rows and update lookup metadata.
   *
   * Intended for lookup background refresh where the table should be fully refreshed or not changed at all.
   */
  async replaceTableDataWithMeta<T = any>(
    tableName: string,
    rows: T[],
    extras?: Pick<TableMeta, 'sourceModifiedEpochMs' | 'lastSyncEpochMs' | 'expiryEpochMs' | 'lastStatus'>
  ): Promise<void> {
    await this.waitForLock();
    this.assertReady();

    const table = this.db!.table<T>(tableName);
    const metaTable = this.db!.table<TableMeta>(META_TABLE);
    const now = Date.now();

    await this.db!.transaction('rw', [table, metaTable], async () => {
      await table.clear();
      if (rows.length > 0) {
        for (let i = 0; i < rows.length; i += LOOKUP_BULK_WRITE_CHUNK_SIZE) {
          const chunk = rows.slice(i, i + LOOKUP_BULK_WRITE_CHUNK_SIZE);
          await table.bulkAdd(chunk);
        }
      }
      await metaTable.put({
        tableName,
        timestamp: now,
        count: rows.length,
        ...extras,
      });
    });
  }

  /**
   * Atomically replace only table rows (no _meta update). Useful when metadata will be committed in a separate final
   * step.
   */
  async replaceTableData<T = any>(tableName: string, rows: T[]): Promise<void> {
    await this.waitForLock();
    this.assertReady();

    const table = this.db!.table<T>(tableName);

    await this.db!.transaction('rw', [table], async () => {
      await table.clear();
      if (rows.length > 0) {
        for (let i = 0; i < rows.length; i += LOOKUP_BULK_WRITE_CHUNK_SIZE) {
          const chunk = rows.slice(i, i + LOOKUP_BULK_WRITE_CHUNK_SIZE);
          await table.bulkAdd(chunk);
        }
      }
    });
  }

  /** Commit metadata for multiple lookup tables in one _meta transaction. */
  async setMetaBatch(
    entries: Array<{
      tableName: string;
      count: number;
      extras?: Pick<TableMeta, 'sourceModifiedEpochMs' | 'lastSyncEpochMs' | 'expiryEpochMs' | 'lastStatus'>;
    }>
  ): Promise<void> {
    if (entries.length === 0) return;

    await this.waitForLock();
    this.assertReady();

    const metaTable = this.db!.table<TableMeta>(META_TABLE);

    await this.db!.transaction('rw', [metaTable], async () => {
      for (const entry of entries) {
        await metaTable.put({
          tableName: entry.tableName,
          timestamp: Date.now(),
          count: entry.count,
          ...(entry.extras || {}),
        });
      }
    });
  }

  /** Delete metadata for a table. */
  async deleteMeta(tableName: string): Promise<void> {
    await this.waitForLock();
    this.assertReady();
    await this.db!.table<TableMeta>(META_TABLE).delete(tableName);
  }

  /**
   * Check if table data is expired.
   *
   * @param tableName - Table to check
   * @param expiryMs - Custom expiry in milliseconds (default: 7 days from CACHE_EXPIRY_MS.DEFAULT)
   * @returns True if expired or no meta exists
   */
  async isExpired(tableName: string, expiryMs: number = CACHE_EXPIRY_MS.DEFAULT): Promise<boolean> {
    await this.waitForLock();
    this.assertReady();
    const meta = await this.getMeta(tableName);
    if (!meta) return true;
    return Date.now() - meta.timestamp > expiryMs;
  }

  /**
   * Check if table has valid (non-expired) data.
   *
   * @param tableName - Table to check
   * @param expiryMs - Custom expiry in milliseconds (default: 7 days from CACHE_EXPIRY_MS.DEFAULT)
   */
  async hasValidData(tableName: string, expiryMs: number = CACHE_EXPIRY_MS.DEFAULT): Promise<boolean> {
    if (!this.tableExists(tableName)) return false;
    const count = await this.count(tableName);
    if (count === 0) return false;
    return !(await this.isExpired(tableName, expiryMs));
  }

  // ===========================================================================
  // Transaction Support
  // ===========================================================================

  /**
   * Execute multiple operations in a single Dexie transaction. All operations succeed or all fail (atomic).
   *
   * @example
   *   ```typescript
   *   await store.transaction('rw', ['assessment', 'member-info'], async (tables) => {
   *     await tables['assessment'].put(assessmentData);
   *     await tables['member-info'].put(memberData);
   *   });
   *   ```;
   *
   * @param mode - 'rw' for read-write, 'r' for read-only
   * @param tableNames - Tables to include in the transaction
   * @param operation - Async function that performs the operations
   * @returns Result of the operation function
   */
  async transaction<T>(
    mode: 'rw' | 'r',
    tableNames: string[],
    operation: (tables: Record<string, any>) => Promise<T>
  ): Promise<T> {
    await this.waitForLock();
    this.assertReady();

    const db = this.db!;
    const tables = tableNames.map((name) => db.table(name));

    return db.transaction(mode, tables, async () => {
      const tableMap: Record<string, any> = {};
      tableNames.forEach((name, i) => {
        tableMap[name] = tables[i];
      });
      return operation(tableMap);
    });
  }

  /**
   * Atomic bulk write across multiple tables. All writes succeed or all fail.
   *
   * @example
   *   ```typescript
   *   await store.atomicBulkWrite([
   *     { table: 'assessment', key: 123, data: assessmentData },
   *     { table: 'member-info', key: 123, data: memberData },
   *   ]);
   *   ```;
   *
   * @param writes - Array of write operations
   */
  async atomicBulkWrite(writes: Array<{ table: string; key: any; data: unknown }>): Promise<void> {
    if (writes.length === 0) return;

    const tableNames = [...new Set(writes.map((w) => w.table))];

    await this.transaction('rw', tableNames, async (tables) => {
      for (const write of writes) {
        await tables[write.table].put(write.data);
      }
    });
  }

  // ===========================================================================
  // Private Helpers
  // ===========================================================================

  private assertReady(): void {
    if (!this.isReady()) {
      throw new Error('[IndexedDBStore] Not initialized. Call initialize() first.');
    }
  }

  /** Wait for any in-progress addTable operations to complete before accessing the DB */
  private async waitForLock(): Promise<void> {
    const g = this.global;
    while (g.addTableLock) {
      const lock = g.addTableLock;
      await lock;
      if (g.addTableLock === lock) break;
    }
  }

  /**
   * Wraps an IndexedDB operation with lock waiting and automatic retry if Dexie closed or re-opened the database
   * connection during a concurrent schema upgrade.
   */
  private async executeWithRetry<T>(operation: () => Promise<T>): Promise<T> {
    await this.waitForLock();
    if (!this.isReady()) {
      await this.initialize().catch(() => {});
    }
    if (this.global.db && !this.global.db.isOpen()) {
      await this.global.db.open().catch(() => {});
    }
    this.assertReady();
    try {
      return await operation();
    } catch (err: any) {
      if (
        err?.name === 'DatabaseClosedError' ||
        err?.name === 'InvalidStateError' ||
        String(err?.message || '').includes('database connection is closing') ||
        String(err?.message || '').includes('DatabaseClosedError')
      ) {
        await this.waitForLock();
        if (this.global.db && !this.global.db.isOpen()) {
          await this.global.db.open().catch(() => {});
        }
        return await operation();
      }
      throw err;
    }
  }

  private buildFullSchema(): Record<string, string> {
    const schema: Record<string, string> = { [META_TABLE]: 'tableName' };

    this.global.schemas.forEach((cfg, tableName) => {
      schema[tableName] = [cfg.primaryKey, ...(cfg.indexes || [])].join(', ');
    });

    return schema;
  }
}
